const batches = new Map()

class PublicationViewBatch {
  constructor(endpoint) {
    this.endpoint = endpoint
    this.submitted = new Set()
    this.clients = 0
    this.visibilityChanged = () => this.schedule()
    this.reset()
  }

  reset() {
    clearTimeout(this.timer)
    this.timer = null
    this.request?.abort()
    this.request = null
    this.pending = new Set()
    this.attempts = new Map()
  }

  connect() {
    if (this.clients++ === 0) document.addEventListener("visibilitychange", this.visibilityChanged)
    return this
  }

  disconnect() {
    if (--this.clients > 0) return
    document.removeEventListener("visibilitychange", this.visibilityChanged)
    this.reset()
  }

  add(id) {
    if (this.submitted.has(id) || this.attempts.has(id)) return
    this.pending.add(id)
    this.schedule()
  }

  schedule() {
    if (!this.clients || this.timer || this.request || !this.pending.size || document.hidden) return
    this.timer = setTimeout(() => {
      this.timer = null
      this.flush()
    }, 1000)
  }

  async flush() {
    if (!this.clients || document.hidden) return
    const ids = Array.from(this.pending)
    if (!ids.length) return
    ids.forEach(id => {
      this.pending.delete(id)
      this.attempts.set(id, (this.attempts.get(id) || 0) + 1)
    })
    const request = new AbortController()
    this.request = request
    const groups = []
    for (let offset = 0; offset < ids.length; offset += 50) groups.push(ids.slice(offset, offset + 50))
    await Promise.all(groups.map(group => this.submit(group, request)))
    if (this.request === request) {
      this.request = null
      this.schedule()
    }
  }

  async submit(ids, request) {
    let retry = false
    try {
      const response = await fetch(this.endpoint, {
        method: "POST",
        credentials: "same-origin",
        redirect: "error",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ publication_ids: ids }),
        signal: request.signal
      })
      if (this.request !== request) return
      if (response.ok && !response.redirected) ids.forEach(id => this.submitted.add(id))
      else retry = !response.redirected && (response.status >= 500 || response.status === 429 || response.status === 408)
    } catch (error) {
      retry = error.name !== "AbortError"
    } finally {
      if (this.request === request && retry && this.clients) {
        ids.filter(id => this.attempts.get(id) < 2).forEach(id => this.pending.add(id))
      }
    }
  }
}

export function connectPublicationViewBatch(endpoint, viewerId) {
  const key = JSON.stringify([endpoint, viewerId])
  if (!batches.has(key)) batches.set(key, new PublicationViewBatch(endpoint))
  return batches.get(key).connect()
}
